/**
 * Per-lot credit management API wrapper (v2 — Project Independence, DNKADM-0059).
 *
 * Re-points the v1 per-lot ops to the v2 **bearer-only** `/v2/admin/members/{c}/
 * credits/**` surface (handoff §5.14), now keyed by **`creditBucketId`** (the 0052
 * `v2_credit_bucket` id) — NOT a service-category string as in v1. Complements the
 * whole-/multi-bucket adjust (`members.ts`) with surgical per-lot operations: read a
 * bucket's **lot breakdown** and then act on lots (batch-set expiry, extend one
 * lot's expiry, or reduce one lot's credits).
 *
 * Bearer-only `/v2/admin/**` surface — `request()` routes it (DNKADM-0049
 * `isV2Surface`): bearer token, NO CSRF, NO cookies, the bounded 401→refresh→retry;
 * the mutating-audit `X-Session-Mode` stamp fires on the writes (`isV2AdminSurface`).
 *
 * Parsed DEFENSIVELY, mirroring `members.ts`: a missing or wrong-typed field never
 * throws; it degrades to `null`. The per-lot ops key on a lot id, so a breakdown
 * entry with NO usable id is DROPPED (never faked) — it cannot be operated on.
 *
 * **Endpoints (handoff §5.14, §3.1 — standard REST; ASSUMED shapes, recorded in the
 * DNKADM-0059 delivery's Notes; batch expiry from DNKAPI-0296):**
 *  - `GET   /v2/admin/members/{c}/credits/{creditBucketId}/lots` → {@link CreditLotsBreakdown}.
 *  - `PUT   /v2/admin/members/{c}/credits/{creditBucketId}/lots/expiry` ← `{ lotIds, expiresAt }`.
 *  - `PUT   /v2/admin/members/{c}/credits/{bucketId}/lots/{lotId}/expiry` ← `{ expiresAt, reason }`.
 *  - `PUT   /v2/admin/members/{c}/credits/{bucketId}/lots/{lotId}/reduce` ← `{ amount, reason }`.
 *
 * `lotId` (the operable handle): the parse accepts `lotId` first, then `id` /
 * `creditLotId` / `creditLotID` as forward-compatible fallbacks. `expiresAt: null`
 * is the sole "never expires" signal; `renews` is independent plan-refill metadata.
 * A depleted lot (remaining 0) is omitted by the server.
 */
import { request } from './client';
import { ApiError } from './errors';

export type RenewalCadence =
  | 'WEEKLY'
  | 'MONTHLY'
  | 'QUARTERLY'
  | 'ANNUAL'
  | (string & {});

type GrantOrigin =
  | 'PLAN_REFILL'
  | 'PLAN_ADDON'
  | 'PURCHASE'
  | 'IMPORT'
  | 'ADMIN'
  | (string & {});

type PlanCreditMode = 'ADDITIVE' | 'REPLACE';

/**
 * DNKAPI-0648 wire names for a lot's last usable day (DNKADM-0442 U3). The server returns
 * `lastUsableDay` (plain `YYYY-MM-DD`, tenant calendar) on every lot and accepts it on staff
 * expiry writes. ASSUMED names, to be confirmed against the real wire: keep every use of them
 * here so a rename is a one-line change.
 *  - `read`: the field on a lot (and, as `ledgerRead`, on a credit-ledger row).
 *  - `write`: the field on a single/batch lot expiry write and on an adjust-credits entry.
 *  - `moveTo`: the target field on an expiry-move body (`toExpiresAt`'s plain-day twin).
 */
export const LOT_DAY_WIRE = {
  read: 'lastUsableDay',
  ledgerRead: 'lotLastUsableDay',
  write: 'lastUsableDay',
  moveTo: 'toLastUsableDay',
} as const;

const PLAIN_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** A plain `YYYY-MM-DD` the API sent, or null (absent, null or malformed: degrade, never throw). */
export function asPlainDay(value: unknown): string | null {
  return typeof value === 'string' && PLAIN_DAY.test(value.trim()) ? value.trim() : null;
}

/**
 * Does this lot data come from an API that sends the last usable day (DNKAPI-0648)? The
 * write path uses this as its capability signal: plain-day writes only when EVERY dated lot
 * carries the day. No dated lot (empty, or all never-expire) is "unknown" and keeps the
 * legacy instant write, which the server still accepts for one release.
 */
export function lotsCarryLastUsableDay(lots: readonly CreditLot[]): boolean {
  const dated = lots.filter((lot) => lot.expiresAt !== null);
  return dated.length > 0 && dated.every((lot) => lot.lastUsableDay !== null);
}

/**
 * One credit lot in a bucket's breakdown. `lotId` is the operable handle. `expiresAt:
 * null` means the lot never expires; the other nullable fields degrade to `null`.
 */
export interface CreditLot {
  /** Owning credit-bucket id, stamped from the bucket-scoped fetch when absent on the wire. */
  bucketId: string | null;
  /** The lot's operable id (resolved via the candidate-key list, `lotId` first). */
  lotId: string;
  /** Credits still available in THIS lot (the source of truth). */
  remaining: number;
  /** Credits originally granted to this lot ("X of Y remaining"). */
  original: number;
  /** ISO-8601 expiry instant, or `null` for "never expires". */
  expiresAt: string | null;
  /**
   * The lot's last usable day as a plain tenant-calendar `YYYY-MM-DD` (DNKAPI-0648), or
   * `null` when the API does not send it (older API) or the lot never expires. Displayed
   * as-is: never converted through any time zone.
   */
  lastUsableDay: string | null;
  /** ISO-8601 grant instant, or `null` when absent. */
  grantedAt: string | null;
  /**
   * Origin of the lot — the trustworthy DNKAPI-0193 uppercase enum (`PURCHASE` /
   * `PACKAGE` / `REFILL` / `ADMIN` / `IMPORT`), or `null`. Parsed as-is (trimmed); the
   * FE owns the display label (case-insensitive mapping, see `LotBreakdownPanel`). This
   * is the lot's OWN enum — distinct from the v2 ledger-reason tokens.
   */
  source: string | null;
  /** Plan renewal metadata: true means this lot auto-refills on plan renewal. */
  renews: boolean;
  /** Plan renewal cadence, uppercased when present; unknown strings are preserved. */
  renewalCadence: RenewalCadence | null;
  /** Owning plan id for plan-sourced lots, or null for non-plan / unbackfilled lots. */
  planId: string | null;
  /** Owning plan display name, or null when absent. */
  planName: string | null;
  /** DNKAPI-0296 grant-origin category, uppercased when present. */
  grantOrigin: GrantOrigin | null;
  /** Plan credit behavior for renewing plan lots; unknown/malformed values degrade to null. */
  planCreditMode: PlanCreditMode | null;
}

/** Parsed `GET .../credits/{creditBucketId}/lots` breakdown — only the active lots. */
export interface CreditLotsBreakdown {
  /** The bucket id echoed back (the `creditBucketId` the breakdown is for). */
  creditBucketId: string | null;
  /** Human label for the bucket (e.g. "Lesson"), or `null` when absent. */
  label: string | null;
  /** The bucket total across its active lots (server-truthful), or `null`. */
  total: number | null;
  /** The active lots (depleted lots are omitted by the server). */
  lots: CreditLot[];
}

// --- Defensive parsing helpers (mirror members.ts) -----------------------------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * A stable string id from a NUMBER or a string (DNKADM-0064): the echoed
 * `creditBucketId` fk arrives as a number on the wire and is normalized to string. The
 * lot's own `lotId` is already number-tolerant via {@link resolveLotId} (it accepts a
 * numeric DB id or a string/uuid handle), so it needs no change here.
 */
function asId(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  return asText(value);
}

/** A real boolean, or `false` when absent / non-boolean (defensive, DNKAPI-0193). */
function asBoolean(value: unknown): boolean {
  return typeof value === 'boolean' ? value : false;
}

/** A trimmed non-empty uppercase string, or null. */
function asUpperText(value: unknown): string | null {
  const text = asText(value);
  return text === null ? null : text.toUpperCase();
}

function asPlanCreditMode(value: unknown): PlanCreditMode | null {
  const upper = asUpperText(value);
  return upper === 'ADDITIVE' || upper === 'REPLACE' ? upper : null;
}

/** A finite number (accepts numeric strings), or null. */
function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/** Candidate keys for the lot id, in priority order (`lotId` first). */
const LOT_ID_KEYS = ['lotId', 'id', 'creditLotId', 'creditLotID'] as const;

/** Resolve the lot id from the candidate keys; null when none is usable. */
function resolveLotId(raw: Record<string, unknown>): string | null {
  for (const key of LOT_ID_KEYS) {
    const v = raw[key];
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
    const text = asText(v);
    if (text !== null) return text;
  }
  return null;
}

/**
 * Parse one lot entry. Returns `null` (→ DROPPED by the caller) when no usable lot id
 * is present — the per-lot ops cannot target an id-less lot, and we never fake one.
 */
function parseLot(raw: unknown): CreditLot | null {
  if (!isObject(raw)) return null;
  const lotId = resolveLotId(raw);
  if (lotId === null) return null;
  return {
    bucketId: asId(raw['bucketId']),
    lotId,
    remaining: asNumber(raw['remaining']) ?? 0,
    original: asNumber(raw['original']) ?? 0,
    expiresAt: asText(raw['expiresAt']),
    lastUsableDay: asPlainDay(raw[LOT_DAY_WIRE.read]),
    grantedAt: asText(raw['grantedAt']),
    source: asText(raw['source']),
    renews: asBoolean(raw['renews']),
    renewalCadence: asUpperText(raw['renewalCadence']),
    planId: asId(raw['planId']),
    planName: asText(raw['planName']),
    grantOrigin: asUpperText(raw['grantOrigin']),
    planCreditMode: asPlanCreditMode(raw['planCreditMode']),
  };
}

/** Parse the `lots` array defensively: a non-array yields `[]`; id-less entries drop. */
function parseLots(raw: unknown): CreditLot[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(parseLot).filter((lot): lot is CreditLot => lot !== null);
}

/** Preserve a server-provided bucket id; otherwise stamp the known request scope. */
function stampBucketId(lot: CreditLot, bucketId: string): CreditLot {
  return lot.bucketId === null ? { ...lot, bucketId } : lot;
}

// --- Endpoints -----------------------------------------------------------------

/**
 * `GET /v2/admin/members/{contactId}/credits/{creditBucketId}/lots` → the bucket's
 * active-lot breakdown (bucket-ID-keyed). Bearer-only `/v2/admin/**`. Throws a typed
 * `ApiError` (e.g. `403` → "needs OWNER/ADMIN"). Parses defensively: every field
 * degrades, and an id-less lot is dropped (it cannot be operated on).
 */
export async function listLots(
  contactId: string,
  creditBucketId: string,
): Promise<CreditLotsBreakdown> {
  const body = await request<unknown>(
    'GET',
    `/v2/admin/members/${encodeURIComponent(contactId)}/credits/${encodeURIComponent(creditBucketId)}/lots`,
  );
  const lotsRaw = Array.isArray(body) ? body : isObject(body) ? body['lots'] : undefined;
  const o = isObject(body) && !Array.isArray(body) ? body : {};
  const lots = parseLots(lotsRaw).map((lot) => stampBucketId(lot, creditBucketId));
  return {
    // Nested credit-bucket fk echo — numeric on the wire (DNKADM-0064); the caller's
    // string arg is the final fallback.
    creditBucketId: asId(o['creditBucketId']) ?? asId(o['bucketId']) ?? creditBucketId,
    label: asText(o['label']),
    total: asNumber(o['total']),
    lots,
  };
}

export interface SetLotsExpiryInput {
  /** Operable lot ids to update atomically inside one credit bucket. */
  lotIds: string[];
  /** A non-null future ISO-8601 instant. The batch endpoint rejects null. */
  expiresAt: string;
}

/**
 * `PUT /v2/admin/members/{contactId}/credits/{creditBucketId}/lots/expiry` → the
 * refreshed active lots for the bucket. Bearer-only `/v2/admin/**`. The backend is
 * atomic: a bad/foreign lot id fails the whole batch and `request()` surfaces the
 * typed `ApiError`.
 */
export async function setLotsExpiry(
  contactId: string,
  creditBucketId: string,
  input: SetLotsExpiryInput,
): Promise<CreditLot[]> {
  if (input.lotIds.length === 0 || asText(input.expiresAt) === null) {
    throw new ApiError('VALIDATION', 400);
  }

  const body = await request<unknown>(
    'PUT',
    `/v2/admin/members/${encodeURIComponent(contactId)}/credits/${encodeURIComponent(creditBucketId)}/lots/expiry`,
    { json: { lotIds: input.lotIds, expiresAt: input.expiresAt } },
  );
  const lotsRaw = Array.isArray(body) ? body : isObject(body) ? body['lots'] : undefined;
  return parseLots(lotsRaw);
}

/** Body for an expiry redistribution. Omitted source means the whole bucket. */
export interface MoveCreditExpiryInput {
  /** Source expiry group; explicit `null` selects the never-expires group. */
  fromExpiresAt?: string | null;
  /** Positive credits to move; omitted moves the whole selected group/bucket. */
  amount?: number;
  /** Future ISO-8601 target, or `null` to make the moved credits never expire. */
  toExpiresAt?: string | null;
  /** DNKAPI-0648: a plain `YYYY-MM-DD` target day, sent INSTEAD of `toExpiresAt`. */
  toLastUsableDay?: string;
  /** Client-known source total used only for preflight validation; never sent. */
  sourceAvailable?: number;
  /**
   * DNKADM-0479 (F-244): optional retry key, one per move the desk means once, re-sent
   * unchanged after an unknown outcome; dink-api (DNKAPI-0695) then moves nothing again. A
   * dink-api without 0695 ignores the field. Sent only when set.
   */
  idempotencyKey?: string;
}

/** Throw the same typed `400` shape as the transport for client-side preflight failures. */
function expiryMoveValidation(message: string): ApiError {
  return new ApiError('VALIDATION', 400, { details: { message } });
}

/**
 * `POST /v2/admin/members/{contactId}/credits/{bucketId}/expiry-move` redistributes
 * credits between expiry groups and returns the bucket's full refreshed lot list.
 * `fromExpiresAt: null` is intentionally distinct from an omitted field: the former
 * selects the never-expires group while the latter selects the whole bucket.
 */
export async function moveCreditExpiry(
  contactId: string,
  bucketId: string,
  input: MoveCreditExpiryInput,
): Promise<CreditLot[]> {
  const hasSource = input.fromExpiresAt !== undefined;
  const amount = input.amount;
  const hasAmount = amount !== undefined;

  if (amount !== undefined && (!Number.isFinite(amount) || amount <= 0)) {
    throw expiryMoveValidation('Amount must be greater than zero.');
  }
  if (hasAmount && !hasSource) {
    throw expiryMoveValidation('Choose a source expiry group before entering an amount.');
  }
  if (amount !== undefined && input.sourceAvailable !== undefined && amount > input.sourceAvailable) {
    throw expiryMoveValidation('Amount cannot exceed the source group available credits.');
  }
  const toDay = input.toLastUsableDay;
  // A plain-day target is validated by the server on the tenant calendar; the client cannot
  // check "future" without the zone math this path exists to avoid.
  if (toDay === undefined && input.toExpiresAt === undefined) {
    throw expiryMoveValidation('Choose a target expiry.');
  }
  if (toDay === undefined && input.toExpiresAt !== null && input.toExpiresAt !== undefined) {
    const targetTime = Date.parse(input.toExpiresAt);
    if (!Number.isFinite(targetTime) || targetTime <= Date.now()) {
      throw expiryMoveValidation('Target expiry must be a future date.');
    }
  }

  const json: Record<string, unknown> =
    toDay !== undefined ? { [LOT_DAY_WIRE.moveTo]: toDay } : { toExpiresAt: input.toExpiresAt };
  if (hasSource) json['fromExpiresAt'] = input.fromExpiresAt;
  if (hasAmount) json['amount'] = amount;
  if (input.idempotencyKey !== undefined) json['idempotencyKey'] = input.idempotencyKey;

  const body = await request<unknown>(
    'POST',
    `/v2/admin/members/${encodeURIComponent(contactId)}/credits/${encodeURIComponent(bucketId)}/expiry-move`,
    { json },
  );
  const lotsRaw = Array.isArray(body) ? body : isObject(body) ? body['lots'] : undefined;
  return parseLots(lotsRaw).map((lot) => stampBucketId(lot, bucketId));
}

/** Body for an extend-expiry op. `expiresAt: null` = never; `reason` non-blank. */
export interface ExtendLotExpiryInput {
  /** A FUTURE ISO-8601 instant to move the lot's expiry to, or `null` for never. */
  expiresAt: string | null;
  /**
   * DNKAPI-0648: a plain `YYYY-MM-DD` last usable day. When set it is sent INSTEAD of
   * `expiresAt` (the server converts it with the lot convention); no instant is computed.
   */
  lastUsableDay?: string;
  /** Required, non-blank audit reason. */
  reason: string;
}

/**
 * `PUT /v2/admin/members/{contactId}/credits/{bucketId}/lots/{lotId}/expiry` → the updated
 * `CreditLot` (same `lotId`, new `expiresAt`). Bearer-only `/v2/admin/**`. Throws a
 * typed `ApiError`: `400` (past `expiresAt` / blank reason), `403`, `404`. Parses the
 * `200` with the shared lot parser (an id-less/unparseable body → a typed error).
 */
export async function extendLotExpiry(
  contactId: string,
  bucketId: string,
  lotId: string,
  input: ExtendLotExpiryInput,
): Promise<CreditLot> {
  const body = await request<unknown>(
    'PUT',
    `/v2/admin/members/${encodeURIComponent(contactId)}/credits/${encodeURIComponent(bucketId)}/lots/${encodeURIComponent(lotId)}/expiry`,
    {
      json:
        input.lastUsableDay !== undefined
          ? { [LOT_DAY_WIRE.write]: input.lastUsableDay, reason: input.reason }
          : { expiresAt: input.expiresAt, reason: input.reason },
    },
  );
  const lot = parseLot(body);
  if (lot === null) throw new ApiError('UNKNOWN', 200);
  return stampBucketId(lot, bucketId);
}

/** Body for a reduce op. `amount` positive (≤ the lot's remaining); `reason` non-blank. */
export interface ReduceLotInput {
  /** A POSITIVE number of credits to remove from THIS lot only (no spill). */
  amount: number;
  /** Required, non-blank audit reason. */
  reason: string;
  /**
   * DNKADM-0479 (F-244): optional retry key, one per reduction the desk means once, re-sent
   * unchanged after an unknown outcome; dink-api (DNKAPI-0693) then removes nothing again.
   * Sent only when set.
   */
  idempotencyKey?: string;
}

/**
 * `PUT /v2/admin/members/{contactId}/credits/{bucketId}/lots/{lotId}/reduce` removes
 * from THIS lot only (no spill). The shipped `200` returns the bucket's lots; a legacy
 * single-lot body is also tolerated. The wrapper selects the targeted lot for the
 * existing dialog callback, or returns `null` when the valid returned list omits a
 * now-inactive depleted target so the caller refetches. Bearer-only `/v2/admin/**`.
 * Throws a typed `ApiError`: `400`, `403`, `404`, `409` (amount > remaining).
 */
export async function reduceLot(
  contactId: string,
  bucketId: string,
  lotId: string,
  input: ReduceLotInput,
): Promise<CreditLot | null> {
  const body = await request<unknown>(
    'PUT',
    `/v2/admin/members/${encodeURIComponent(contactId)}/credits/${encodeURIComponent(bucketId)}/lots/${encodeURIComponent(lotId)}/reduce`,
    {
      json: {
        amount: input.amount,
        reason: input.reason,
        ...(input.idempotencyKey !== undefined ? { idempotencyKey: input.idempotencyKey } : {}),
      },
    },
  );
  const directLot = parseLot(body);
  if (directLot !== null) return stampBucketId(directLot, bucketId);

  const lotsRaw = Array.isArray(body) ? body : isObject(body) ? body['lots'] : undefined;
  if (!Array.isArray(lotsRaw)) throw new ApiError('UNKNOWN', 200);
  const target = parseLots(lotsRaw).find((lot) => lot.lotId === lotId);
  return target === undefined ? null : stampBucketId(target, bucketId);
}
